import React, { useEffect, useRef, useState } from 'react';
import ActionButton from '@applied/dragonfruit/src/components/ActionButton/ActionButton';

const LOGIN_FORM_WIDTH_PX = 400;
const GOOGLE_CLIENT_ID = '831541393360-qs68q85d62v6dlm0s1occou5p8gh9rka.apps.googleusercontent.com';

export interface AppliedGoogleLoginProps {
  context: 'signin_with' | 'signup_with';
  onSuccess: (credential: string) => void;
  onError: () => void;
}

/**
 * This is a light handrolled wrapper around the Google Identity Services client.
 *
 * There are npm packages that do this for us, but the code required is here quite lightweight
 * and we avoid the cruft and can more easily customize the UI this way.
 */
function AppliedGoogleLogin({ context, onSuccess, onError }: AppliedGoogleLoginProps): JSX.Element {
  const buttonText = context === 'signin_with' ? 'Login with Google' : 'Sign up with Google';
  const [gsiScriptLoaded, setGsiScriptLoaded] = useState(false);
  const googleButtonRef = useRef<HTMLDivElement>(null);

  // The first thing we need to do is load the gsi script, which makes
  // the google object available.
  // Technically this is an async operation, so nothing will be rendered
  // until the script is loaded.
  // See: https://developers.google.com/identity/gsi/web/guides/get-google-api-clientid#load_the_client_library
  useEffect(function loadGsiScript() {
    const scriptTag = document.createElement('script');
    scriptTag.src = 'https://accounts.google.com/gsi/client';
    scriptTag.async = true;
    scriptTag.defer = true;
    scriptTag.onload = (): void => {
      setGsiScriptLoaded(true);
    };
    scriptTag.onerror = (): void => {
      setGsiScriptLoaded(false);
      console.error('Failed to load Google Identity Services client');
    };

    document.body.appendChild(scriptTag);

    return function removeGsiScript(): void {
      document.body.removeChild(scriptTag);
    };
  }, []);

  // After the script has been loaded, we run some google api calls that
  // render the button.
  // See: https://developers.google.com/identity/gsi/web/guides/display-button#button_rendering
  useEffect(
    function createGsiButton() {
      if (!gsiScriptLoaded || !google || !googleButtonRef.current) {
        return;
      }
      google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: ({ credential }) => {
          if (!credential) {
            console.error('No credential returned from Google');
            onError();
            return;
          }
          onSuccess(credential);
        },
      });
      google.accounts.id.renderButton(googleButtonRef.current, {
        text: context,
        theme: 'outline',
        size: 'large',
        type: 'standard',
        // Note: this value must be exactly the same width as the .login-form class.
        width: LOGIN_FORM_WIDTH_PX,
      });
    },
    [gsiScriptLoaded, context, onSuccess, onError],
  );

  return (
    <>
      {/*
       * HACK: Couldn't figure out a way to get the signin to work without clicking the button google renders for us via `renderButton`.
       * Ideally, we just render the button and call the login api directly.
       */}
      <div style={{ display: 'none' }}>
        <div ref={googleButtonRef} />
      </div>
      <ActionButton
        onClick={() => {
          const googleLoginButton = googleButtonRef.current?.querySelector('[role="button"]');
          if (!googleLoginButton || !(googleLoginButton instanceof HTMLElement)) {
            console.error('Google login button not found');
            onError();
            return;
          }
          googleLoginButton.click();
        }}
        disabled={!gsiScriptLoaded}
        iconBeforeProps={{ src: 'GOOGLE', height: 24 }}
        shouldFillContainer
        size="large"
        shouldCenterText
      >
        {buttonText}
      </ActionButton>
    </>
  );
}

export { AppliedGoogleLogin };
